// /admin/tenants — operator console: the client list. Composes the
// design-system DataTable + FilterBar + Pagination. Search is accent-insensitive
// (Mexican Spanish), the list filters by vertical/plan/estado and sorts on any
// column. Data comes from the listTenants() seam → GET /admin/tenants (real,
// lands).

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  listTenants,
  PLAN_LABEL,
  STATUS_BADGE,
  type Tenant,
  type TenantPlan,
  type TenantStatus,
  type TenantVertical,
  VERTICAL_LABEL,
} from './tenants-data.js'
import {
  Button,
  Chip,
  DataTable,
  type DataTableColumn,
  FilterBar,
  FilterButton,
  IconButton,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from '../../design-system/index.js'
import { foldForSearch, matchesSearch } from '../../lib/search.js'

const PAGE_SIZE = 8

type SortDir = 'asc' | 'desc'
interface SortState {
  key: string
  dir: SortDir
}

const PLAN_ORDER: Record<TenantPlan, number> = { basico: 0, profesional: 1, empresarial: 2 }
// Money trouble sorts first — the list is a worklist, not an alphabet.
const STATUS_ORDER: Record<TenantStatus, number> = {
  past_due: 0,
  sin_suscripcion: 1,
  onboarding: 2,
  courtesy: 3,
  active: 4,
}

// What each sortable column orders by — folded text for names (so sort matches
// search), an explicit rank for the categorical columns, the raw number for
// conversations.
function sortValue(t: Tenant, key: string): string | number {
  switch (key) {
    case 'name':
      return foldForSearch(t.name)
    case 'vertical':
      return VERTICAL_LABEL[t.vertical]
    case 'plan':
      return PLAN_ORDER[t.plan]
    case 'status':
      return STATUS_ORDER[t.status]
    case 'am':
      return foldForSearch(t.accountManager.name)
    case 'conversations30d':
      return t.conversations30d
    default:
      return ''
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

const VERTICAL_OPTIONS = [
  { value: '', label: 'Todas las verticales' },
  ...(Object.keys(VERTICAL_LABEL) as TenantVertical[]).map((v) => ({
    value: v,
    label: VERTICAL_LABEL[v],
  })),
]
const PLAN_OPTIONS = [
  { value: '', label: 'Todos los planes' },
  ...(Object.keys(PLAN_LABEL) as TenantPlan[]).map((p) => ({ value: p, label: PLAN_LABEL[p] })),
]
const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  ...(Object.keys(STATUS_BADGE) as TenantStatus[]).map((s) => ({
    value: s,
    label: STATUS_BADGE[s].label,
  })),
]

export function TenantsPage(): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Seeded from ?q= so "Ver portafolio" on a manager lands here already filtered
  // to their clients (the table searches the account-manager name).
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [vertical, setVertical] = useState<TenantVertical | ''>('')
  const [plan, setPlan] = useState<TenantPlan | ''>('')
  const [status, setStatus] = useState<TenantStatus | ''>('')
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)

  const {
    data: tenants = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: listTenants,
  })

  function refresh(): void {
    void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
  }

  const hasFilters = query.trim() !== '' || vertical !== '' || plan !== '' || status !== ''

  const filtered = useMemo(() => {
    const rows = tenants.filter(
      (t) =>
        (matchesSearch(t.name, query) || matchesSearch(t.accountManager.name, query)) &&
        (vertical === '' || t.vertical === vertical) &&
        (plan === '' || t.plan === plan) &&
        (status === '' || t.status === status),
    )
    const dir = sort.dir === 'asc' ? 1 : -1
    return rows.sort((a, b) => {
      const va = sortValue(a, sort.key)
      const vb = sortValue(b, sort.key)
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), 'es') * dir
    })
  }, [tenants, query, vertical, plan, status, sort])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // Sorting on a new column starts ascending; clicking the active column flips it.
  function onSort(key: string): void {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    )
    setPage(1)
  }

  // The mobile sort control (cards have no sortable headers): pick a column,
  // keep the chosen direction; the toggle flips it.
  function changeSortKey(key: string): void {
    setSort((s) => ({ key, dir: s.dir }))
    setPage(1)
  }
  function toggleSortDir(): void {
    setSort((s) => ({ key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }))
    setPage(1)
  }

  function clearFilters(): void {
    setQuery('')
    setVertical('')
    setPlan('')
    setStatus('')
    setPage(1)
  }

  const columns: DataTableColumn<Tenant>[] = [
    {
      key: 'name',
      label: 'Cliente',
      sortable: true,
      render: (t) => (
        <button
          type="button"
          onClick={() => void navigate(`/admin/tenants/${t.id}`)}
          className="flex items-center gap-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          <span className="grid size-8 flex-none place-items-center rounded-lg bg-field text-[11px] font-bold text-text-2">
            {initialsOf(t.name)}
          </span>
          <span className="font-semibold text-text hover:text-link">{t.name}</span>
        </button>
      ),
    },
    {
      key: 'vertical',
      label: 'Vertical',
      sortable: true,
      render: (t) => <Chip>{VERTICAL_LABEL[t.vertical]}</Chip>,
    },
    {
      key: 'plan',
      label: 'Plan',
      sortable: true,
      render: (t) => <span className="text-text-2">{PLAN_LABEL[t.plan]}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      render: (t) => {
        const s = STATUS_BADGE[t.status]
        return (
          <span className="flex flex-wrap items-center gap-1.5">
            {t.suspended ? (
              <Chip tone="warn" dot>
                Suspendido
              </Chip>
            ) : (
              <Chip tone={s.tone} dot>
                {s.label}
              </Chip>
            )}
            {t.ownerInvitePending ? <Chip tone="warn">Invitación pendiente</Chip> : null}
          </span>
        )
      },
    },
    {
      key: 'am',
      label: 'Gerente de cuenta',
      sortable: true,
      render: (t) => (
        <span className="flex items-center gap-2">
          <span className="grid size-7 flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[10px] font-bold text-white">
            {initialsOf(t.accountManager.name)}
          </span>
          <span className="text-text-2">{t.accountManager.name}</span>
        </span>
      ),
    },
    {
      key: 'conversations30d',
      label: 'Conversaciones (30 d)',
      align: 'right',
      sortable: true,
      render: (t) => (
        <span className="tabular-nums">{t.conversations30d.toLocaleString('es-MX')}</span>
      ),
    },
  ]

  const sortOptions = columns
    .filter((c) => c.sortable)
    .map((c) => ({ value: c.key, label: c.label }))

  return (
    <div data-testid="admin-tenants" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Clientes"
        subtitle="Los negocios que operan en INHERTA."
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              variant="secondary"
              aria-label="Actualizar lista"
              title="Actualizar"
              onClick={refresh}
              disabled={isFetching}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  className={isFetching ? 'animate-spin' : ''}
                >
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <path d="M21 3v6h-6" />
                </svg>
              }
            />
            <Button onClick={() => void navigate('/admin/tenants/new')}>Nuevo cliente</Button>
          </div>
        }
      />

      <div className="mt-5">
        <FilterBar>
          <SearchInput
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPage(1)
            }}
            placeholder="Buscar por nombre o gerente"
            aria-label="Buscar cliente"
          />
          <div className="w-full sm:w-44">
            <Select
              aria-label="Filtrar por vertical"
              options={VERTICAL_OPTIONS}
              value={vertical}
              onChange={(e) => {
                setVertical(e.target.value as TenantVertical | '')
                setPage(1)
              }}
            />
          </div>
          <div className="w-full sm:w-40">
            <Select
              aria-label="Filtrar por plan"
              options={PLAN_OPTIONS}
              value={plan}
              onChange={(e) => {
                setPlan(e.target.value as TenantPlan | '')
                setPage(1)
              }}
            />
          </div>
          <div className="w-full sm:w-44">
            <Select
              aria-label="Filtrar por estado"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as TenantStatus | '')
                setPage(1)
              }}
            />
          </div>
          {hasFilters && <FilterButton onClick={clearFilters}>Limpiar</FilterButton>}
        </FilterBar>

        {/* Mobile-only sort control — the card layout below md has no sortable
            headers, so ordering moves into an explicit picker + direction toggle. */}
        <div className="mt-2 flex items-center gap-2 md:hidden">
          <span className="flex-none text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3">
            Ordenar
          </span>
          <div className="flex-1">
            <Select
              aria-label="Ordenar por"
              options={sortOptions}
              value={sort.key}
              onChange={(e) => changeSortKey(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={toggleSortDir}
            aria-label={
              sort.dir === 'asc'
                ? 'Orden ascendente; cambiar a descendente'
                : 'Orden descendente; cambiar a ascendente'
            }
            className="flex size-10 flex-none items-center justify-center rounded-md border border-line bg-surface text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-[16px]"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className={sort.dir === 'desc' ? 'rotate-180' : ''}
            >
              <path d="M12 5v14M6 11l6-6 6 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="mt-3">
        <DataTable
          caption="Clientes"
          columns={columns}
          rows={pageRows}
          loading={isLoading}
          sort={sort}
          onSort={onSort}
          empty={
            <div className="px-5 py-10 text-center text-[12.5px] text-text-2">
              Ningún cliente coincide con los filtros.
            </div>
          }
          rowActions={(t) => [
            { label: 'Ver detalle', onSelect: () => void navigate(`/admin/tenants/${t.id}`) },
          ]}
        />
      </div>

      <div className="mt-3">
        <Pagination
          page={safePage}
          pageCount={pageCount}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onPage={setPage}
          noun="clientes"
        />
      </div>
    </div>
  )
}
